import { TimeFormat } from '@databases/model/field';
import { DatabaseFieldType } from '@databases/types/database';
import { DatabaseFilterType } from '@databases/types/database/view';
import { format, isValid, parse, parseISO } from 'date-fns';
import {
  cs,
  da,
  de,
  enGB,
  enUS,
  es,
  fi,
  fr,
  hu,
  it,
  ja,
  ko,
  nb,
  nl,
  pl,
  ptBR,
  ru,
  sv,
  tr,
  zhCN,
  zhTW
} from 'date-fns/locale';

import { encodeHtml } from '../../utils';
import type { FieldType } from '../field-types';

export const DATE_VALUE_TODAY = 'today';

const DATE_VALUE_REGEXP = /^\d\d\d\d-\d\d-\d\d$/;
export const DATE_FORMAT = 'yyyy-MM-dd';

export const DATETIME_FORMAT = "yyyy-MM-dd'T'HH:mmXXX";
const DATETIME_VALUE_REGEXP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}([+-]\d{2}:\d{2}|Z)$/;

/** Extracts calendar date (YYYY-MM-DD) in the local timezone from an ISO string. */
export function toCalendarDate(isoString: string): string {
  const date = parseISO(isoString);
  if (isNaN(date.getTime())) {
    return isoString.slice(0, 10);
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function hasTimeComponent(value: string): boolean {
  return DATETIME_VALUE_REGEXP.test(value);
}

export function normalizeDatetimeString(value: string): string {
  const parsed = parseISO(value);
  return isValid(parsed) ? format(parsed, DATETIME_FORMAT) : value;
}

export const dateFnsLocalesMap = {
  de: de,
  'en-GB': enGB,
  'en-US': enUS,
  cs: cs,
  ko: ko,
  'pt-BR': ptBR,
  fr: fr,
  es: es,
  it: it,
  ja: ja,
  nl: nl,
  nb: nb,
  da: da,
  sv: sv,
  fi: fi,
  ru: ru,
  pl: pl,
  tr: tr,
  hu: hu,
  'zh-CN': zhCN,
  'zh-TW': zhTW,
  'en-pseudo': de,
  xx: de,
  en: enUS,
  'en-ZZ': enUS
};

const getDateFnsLocale = (locale: string | undefined) => {
  return locale && locale in dateFnsLocalesMap
    ? dateFnsLocalesMap[locale as keyof typeof dateFnsLocalesMap]
    : undefined;
};

const isValidDate = (date: Date): boolean => {
  return !isNaN(date.getTime());
};

export function isValidDateString(date: string): boolean {
  return (
    date === DATE_VALUE_TODAY ||
    (!!date.match(DATE_VALUE_REGEXP) && isValid(parse(date, DATE_FORMAT, new Date()))) ||
    (DATETIME_VALUE_REGEXP.test(date) && isValid(parseISO(date)))
  );
}

/**
 * Takes a date value, parses it and returns it in YYYY-MM-DD format. If the date string has an invalid
 * format, undefined is returned.
 * @param resolve Unless this is set to false, the "today" virtual value is resolved to the current date.
 * @param includeTime When true and resolving "today", includes the current time in DATETIME_FORMAT.
 */
export function parseDate(
  date: string | undefined,
  includeTime = false
): string | undefined {
  if (!date || !isValidDateString(date)) {
    return undefined;
  } else if (date === DATE_VALUE_TODAY) {
    return format(new Date(), includeTime ? DATETIME_FORMAT : DATE_FORMAT);
  } else {
    return date;
  }
}

export function parseDatetime(date: string | undefined): string | undefined {
  return parseDate(date, true);
}

export function toDisplayDate(
  value: string,
  locale?: string,
  timeFormat?: TimeFormat
): string | undefined {
  const dateFnsLocale = getDateFnsLocale(locale);
  const parsedDate = parseDate(value);

  if (!parsedDate) {
    return undefined;
  }

  if (timeFormat && hasTimeComponent(parsedDate)) {
    const timePattern = timeFormat === TimeFormat.TWELVE_HOUR ? 'PP h:mm a' : 'PP HH:mm';
    return format(parseISO(parsedDate), timePattern, {
      locale: dateFnsLocale
    });
  }

  return format(parseISO(parsedDate), 'PP', {
    locale: dateFnsLocale
  });
}

export function getLocalizedDatePlaceholder(locale: string): string {
  const dateFnsLocale = getDateFnsLocale(locale);
  return format(new Date(), 'P', { locale: dateFnsLocale });
}

function getEncodedDate(value: string): string {
  return `<time datetime="${encodeHtml(value)}">${encodeHtml(value)}</time>`;
}

/**
 * Creates a Date object in local time based on the UTC date.
 * This is most commonly used on Date objects that were created from date
 * strings in ISO format. In this case, he Date object was originally created in
 * UTC time (if no timestamp provided, time is midnight) so we adjust for UTC
 * to create a date in local time. This is needed because format treats the date
 * as being in local time. So ex. a Date in UTC time at midnight is treated as the previous day for timezones
 * behind UTC. As a workaround, we construct a new Date object in local time based on the UTC date.
 * We should not call this on non-ISO Dates as the given Date object will
 * already be in local time. In timezones ahead of UTC, the corresponding UTC
 * time could be a day behind.
 */
export function getDateWithUTCOffset(date: Date): Date {
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/** Parses a date string in ISO format and returns a Date object in midnight
 * local time. We only care about the date as written in the string (ignoring time or timezones).
 * Sometimes parseISO is more lenient than Date constructor (ex. parseISO recognizes
 * YYYY-MMDD as valid while Date constructor does not), so we only treat it as a
 * valid date string if it's recognized by both.
 * We want to use the Date generated by parseISO instead of the Date constructor
 * because parseISO gives us a Date in midnight local time which we need for
 * formatting, while Date constructor with ISO strings gives us a Date in midnight UTC time.
 */
export function getDateFromISOString(dateString: string): Date | undefined {
  const date = parseISO(dateString.split('T')[0]);
  return isValidDate(date) && isValidDate(new Date(dateString)) ? date : undefined;
}

/**
 * Converts the given string to a date string in YYYY-MM-DD format if it's a valid date.
 * @param convertToLocalTime: If true, we will take timestamp into account and convert
 * the given date string to a date in the user's local time. If false, we will
 * ignore the timestamp if given in an ISO string and only take the date.
 */
export function stringToDate(
  value: string,
  convertToLocalTime = false
): string | undefined {
  if (!value.trim()) {
    return undefined;
  }

  // For ISO date strings - if convertToLocalTime is true, we can use the Date constructor to construct a date in UTC time
  // which will be converted to local time with timezone adjustments when calling format. Otherwise, we
  // need to call getDateFromISOString to get a Date that's already in local time if we don't want to apply timezone adjustments.
  let date = convertToLocalTime
    ? new Date(value)
    : (getDateFromISOString(value) ?? new Date(value));
  if (isValidDate(date)) {
    return format(date, DATE_FORMAT);
  }

  // check if it's a unix timestamp
  date = new Date(Number(value)); // Unix timestamp millis
  // known issue: unix timestamp seconds import doesn't work
  if (date.getFullYear() >= 10000) {
    date = new Date(Number(value) / 1000); // Unix timestamp seconds
  }
  return isValidDate(date) ? format(getDateWithUTCOffset(date), DATE_FORMAT) : undefined;
}

const dateFieldType: FieldType<DatabaseFieldType.DATE> = {
  supportedFilterTypes: [
    DatabaseFilterType.EQUALS,
    DatabaseFilterType.EMPTY,
    DatabaseFilterType.GREATER,
    DatabaseFilterType.LESS
  ],

  isCsvImportSupported: true,

  renderExportView: ({ value }) => {
    const parsed = parseDate(value);
    if (parsed) {
      return getEncodedDate(parsed);
    } else {
      return '';
    }
  },
  parseFromHtml: (el) =>
    stringToDate(
      el.find('time').eq(0).attr('datetime') ??
        el.find('.date-node').eq(0).attr('data-timestamp') ??
        el.text().trim()
    ),

  getInitialValue: () => undefined,

  getInitialDefaultValue: () => DATE_VALUE_TODAY,

  isEmptyValue: ({ value }) => !value,

  isEqualValue: ({ value: value1 }, { value: value2 }) => {
    const parsed1 = parseDate(value1);
    const parsed2 = parseDate(value2);
    if (parsed1 === undefined || parsed2 === undefined) {
      return parsed1 === parsed2;
    }
    // Compare by calendar date only (YYYY-MM-DD) to correctly handle mixed
    // date-only and datetime values on the same calendar day.
    // Using parseISO timestamp comparison would fail because date-only strings
    // parse to midnight local time, which differs from any datetime on that day.
    return parsed1.slice(0, 10) === parsed2.slice(0, 10);
  },

  getStringRepresentation: ({ value }) => parseDate(value) ?? '',

  getNumberRepresentation: ({ value }) => {
    const date = parseDate(value);
    return date ? parseISO(date).getTime() : undefined;
  },

  getFilterNumberRepresentation: ({ value }) => {
    const date = parseDate(value);
    if (!date) {
      return undefined;
    }
    return parseISO(toCalendarDate(date)).getTime();
  },

  convertFromCsv: ({ value }) => {
    return stringToDate(value.plain);
  },

  convertFrom: {
    [DatabaseFieldType.TEXT]: ({ value }) => stringToDate(value.plain)
  },

  convertTo: {
    [DatabaseFieldType.TEXT]: ({ value }) => ({
      plain: parseDate(value) ?? ''
    })
  }
};

export default dateFieldType;
